// @flow
import React, { useEffect, useRef, useState } from 'react';
import injectSheet from 'react-jss';
import { gql, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Button from '~/app/ui/Button';
import { Page, PageHeader } from '~/app/ui/Page';
import SearchField from '~/app/ui/SearchField';
import EmptyState from '~/app/ui/EmptyState';
import { S, A } from '~/app/ui/tokens';
import TemplateBrowserItem from './TemplateBrowserItem';

const AIXC_PRESSROOM_PRESET = 'AIXC_PRESSROOM';

const getAixcPressRoomTemplate = (templates = []) => {
  const futureSightTemplate = templates.find(template => template.id === 'FutureSightDetails');

  if (!futureSightTemplate) {
    return null;
  }

  const aixcTemplateSettings = {
    ...(futureSightTemplate.defaultProps?.templateSettings || {}),
    press_kit: '',
    region: 'US',
    section: '',
    url: '/cxc10/us/newsroom/',
    title: 'AIXC Press Room Title',
    description: '',
    heroImageUrl: 'https://genesis-cdn.ff.com/aixcrypto-news/----_20251117231706_5_63-default.jpg',
    featured: false,
    published_at: new Date().toISOString(),
    published_by: 'AIxC Team',
    tags: [],
    social_share: [],
    heroImageUrlMobile: 'https://genesis-cdn.ff.com/aixcrypto-news/aixc-newsroom-default-default.jpg',
    background_position: 'CENTER CENTER',
    background_scale: 'contain',
    responsive_image: false,
    hero_background_color: 'white'
  };

  return {
    ...futureSightTemplate,
    browserId: 'future-sight-details-aixc-pressroom',
    name: 'AIXC Press Room (Newsroom)',
    presetType: AIXC_PRESSROOM_PRESET,
    defaultProps: {
      ...(futureSightTemplate.defaultProps || {}),
      templateSettings: aixcTemplateSettings
    }
  };
};

const keyOf = template => template.browserId || template.id;

type Props = {
  classes: Object
};

/**
 * The first step of a new Genesis page: pick a template. The list is on the
 * left, the chosen template's preview on the right, and "Use this template"
 * stays at the top. Arrow keys move through the list.
 */
const TemplateBrowser = (props: Props) => {
  const { classes: c } = props;

  const { data, loading: isFetching, error, refetch } = useQuery(QUERY);
  const templates = data?.templates;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const listRef = useRef(null);

  const { push } = useHistory();

  const getTemplatesWithPresets = () => {
    if (!templates) {
      return [];
    }

    const aixcPressRoomTemplate = getAixcPressRoomTemplate(templates);

    if (!aixcPressRoomTemplate) {
      return templates;
    }

    return [aixcPressRoomTemplate, ...templates];
  };

  const getFilteredTemplates = () => {
    const templatesWithPresets = getTemplatesWithPresets();
    const query = searchQuery.toLowerCase();
    // What's shown: the template's name (and its id, which is close to it).
    const searchKeys = ['id', 'name', 'presetType'];

    return templatesWithPresets.filter(template => searchKeys.some(key => (template[key] || '').toLowerCase().includes(query)));
  };

  const filteredTemplates = getFilteredTemplates();
  // The chosen template, or the first one in the list.
  const template = filteredTemplates.find(t => keyOf(t) === selectedKey) || filteredTemplates[0];

  // Keep the chosen row in view when moving with the keyboard.
  useEffect(() => {
    const row = listRef.current && template && listRef.current.querySelector(`[data-template="${keyOf(template)}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [template && keyOf(template)]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickSelect = () => {
    if (!template) {
      return;
    }

    const presetQuery = template.presetType ? `?preset=${template.presetType}` : '';

    push(`/admin/page/new/${template.id}${presetQuery}`);
  };

  const onListKeyDown = (event) => {
    const index = filteredTemplates.findIndex(t => template && keyOf(t) === keyOf(template));
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    // Enter on a template opens it; on anything else in the list (the "Clear search" button) it does its own thing.
    if (event.key === 'Enter') {
      if (event.target && event.target.getAttribute && event.target.getAttribute('role') === 'option') { event.preventDefault(); handleClickSelect(); }
      return;
    }
    if (!step || !filteredTemplates.length) return;
    event.preventDefault();
    const next = filteredTemplates[(index + step + filteredTemplates.length) % filteredTemplates.length];
    setSelectedKey(keyOf(next));
    const row = listRef.current && listRef.current.querySelector(`[data-template="${keyOf(next)}"]`);
    if (row) row.focus();
  };

  let content;
  if (error && !data) {
    content = <EmptyState tone="error" title="Couldn’t load the templates" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>;
  } else if (isFetching && !data) {
    content = <EmptyState icon="hourglass_empty" title="Loading templates…" compact />;
  } else {
    content = (
      <div className={c.layout}>
        <div className={c.side}>
          <SearchField value={searchQuery} onChange={setSearchQuery} placeholder="Search templates" width="100%" testId="template-search" />
          <div className={c.count}>{filteredTemplates.length === 1 ? '1 template' : `${filteredTemplates.length} templates`}</div>
          <div className={c.list} ref={listRef} role="listbox" aria-label="Templates" onKeyDown={onListKeyDown} data-testid="template-list">
            {filteredTemplates.map((t) => {
              const selected = template && keyOf(t) === keyOf(template);
              return (
                <button
                  key={keyOf(t)}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  className={c.option}
                  data-template={keyOf(t)}
                  onClick={() => setSelectedKey(keyOf(t))}
                  onDoubleClick={() => { setSelectedKey(keyOf(t)); push(`/admin/page/new/${t.id}${t.presetType ? `?preset=${t.presetType}` : ''}`); }}
                >
                  <span className={c.optionName}>{t.name}</span>
                </button>
              );
            })}
            {filteredTemplates.length === 0 && (
              <EmptyState icon="search_off" title="No templates match" compact action={<Button size="sm" onClick={() => setSearchQuery('')}>Clear search</Button>} />
            )}
          </div>
        </div>
        <div className={c.preview} data-testid="template-preview">
          {template
            ? <TemplateBrowserItem template={template} key={keyOf(template)} />
            : <EmptyState icon="dashboard" title="Pick a template to see it" />}
        </div>
      </div>
    );
  }

  return (
    <Page testId="template-browser">
      <PageHeader
        back={{ to: '/admin/page/new', label: 'New page' }}
        title="Choose a template"
        description="The layout of the new page. You set its title, category and address in the next step."
        actions={(
          <Button variant="primary" icon="arrow_forward" onClick={handleClickSelect} disabled={!template} data-testid="use-template">
            {template ? `Use “${template.name}”` : 'Use this template'}
          </Button>
        )}
      />
      {content}
    </Page>
  );
};

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  layout: {
    display: 'grid',
    gridTemplateColumns: '300px minmax(0, 1fr)',
    gap: 20,
    alignItems: 'start',
    '@media (max-width: 900px)': { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  side: {
    position: 'sticky',
    top: 16,
    display: 'grid',
    gap: 10,
    '@media (max-width: 900px)': { position: 'static' }
  },
  count: { fontSize: 12, color: S.muted, padding: '0 2px' },
  list: {
    display: 'grid',
    gap: 2,
    maxHeight: 'calc(100vh - 260px)',
    overflowY: 'auto',
    padding: 4,
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 12,
    '@media (max-width: 900px)': { maxHeight: 260 }
  },
  option: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'grid',
    gap: 2,
    padding: '9px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    '&:hover': { background: S.chrome },
    '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${S.fg}` },
    '&[aria-selected="true"]': { background: S.fg, color: S.onFg }
  },
  optionName: { fontSize: 14, fontWeight: 600 },
  preview: { minWidth: 0 }
};

/* =============================================================================
=    GraphQL: Get templates
============================================================================= */
const QUERY = gql`
query getTemplates {
  templates {
    id
    name
    defaultProps
  }
}
`;

export default injectSheet(styles)(TemplateBrowser);
